// sourceAdapter — the pure `showResource`-envelope → `LoadedSource` boundary
// reader. Sibling of materialAdapter's `parseShowResource`, same wireShape
// idiom: cast-free structural reads over the untrusted envelope value, total,
// nothing throws.
//
// A source resource arrives as `{ view: { resource: { title, content,
// classification, summary?, … }, timestamps, annotations } }`. The reading
// text is the domain's ONE extracted-content string — the server has no block
// model, so this reader derives presentation blocks itself: split on blank
// lines, ids positional (`block-N`). Those SAME ids are what block-anchored
// annotations use as their part locator, so a block is `highlighted` exactly
// when a note anchors to it. Empty/whitespace content degrades to an honest
// `unavailable` with an author-facing reason instead of failing the view —
// graceful blob degradation, matching the ingest paths that keep only the
// content hash.
//
// The PDF variant is NOT decided here: the envelope carries no media type.
// `httpPorts.loadSource` probes `/resources/:id/original` and upgrades the
// parsed text content to `pdf` when original bytes exist — this reader stays
// a pure function of the envelope.
//
// Author-facing strings here are plain English constants: the port layer
// carries no i18n (the same precedent as httpPorts' "Untitled"/"Workspace");
// the host localises the display of port ERRORS only, so these value-side
// strings ship in English until the ports grow a locale seam.

export { parseSourceContent };

import { Result } from "@bilda/utils";
import type { MaterialRef } from "../components/Navigation/types.js";
import { projectText } from "../components/SourceView/readingShape.js";
import type {
    SourceAnnotation,
    SourceUse,
} from "../components/SourceView/SourceView.js";
import { parseBibliography } from "./bibliography.js";
import { type ExternalError, mkMalformed } from "./externalError.js";
import type { LoadedSource } from "./ports.js";
import { fields, list, num, str } from "./wireShape.js";

// === Boundary reader ==========================================================

const parseSourceContent = (
    sourceId: string,
    value: unknown,
): Result.Result<LoadedSource, ExternalError> => {
    const view = fields(fields(value)?.get("view"));
    const resource = fields(view?.get("resource"));
    if (resource === undefined) {
        return Result.err(mkMalformed("showResource: no resource in view"));
    }
    const title = str(resource.get("title")) ?? "Untitled";
    const annotations = annotationsOf(view);
    return Result.ok({
        meta: {
            // The domain keeps ONE name per source (its title, usually the
            // ingested filename) — no separate origin-filename field exists.
            filename: title,
            kind: kindOf(str(resource.get("classification"))),
            addedOnIso: addedOnIsoOf(view),
            // Rides through so `AnnotatedBlock`'s select→annotate gesture
            // and "Add to context" action can dispatch ports — see
            // `SourceMeta`'s own header for why this is threaded here
            // rather than as a new `AppShellHost`-owned prop.
            sourceId,
        },
        content: toContent(fields(view?.get("extract")), annotations),
        summary: summaryOf(resource),
        bibliography: parseBibliography(
            fields(resource.get("summary"))?.get("bibliography"),
        ),
        annotations,
        // HONEST GAP: no server-side quote-level citation query exists yet
        // (grounding runs material-first), so verbatim evidence stays empty.
        // Lineage does exist: `view.derivedMaterials` carries the materials
        // created from this source.
        evidence: [],
        derivedMaterials: derivedMaterialsOf(view),
        uses: usesOf(view),
    });
};

// `view.uses` lists `{ materialId, materialTitle, node, statement, claimId,
// quote, page? }` (`@bilda/app`'s `SourceUse`). A malformed entry is dropped
// rather than failing the view; an older server sends none.
const usesOf = (
    view: ReadonlyMap<string, unknown> | undefined,
): readonly SourceUse[] =>
    list(view?.get("uses")).flatMap((raw): SourceUse[] => {
        const entry = fields(raw);
        const id = str(entry?.get("materialId"));
        const node = str(entry?.get("node"));
        const statement = str(entry?.get("statement"));
        const claimId = str(entry?.get("claimId"));
        const quote = str(entry?.get("quote"));
        if (
            id === undefined ||
            node === undefined ||
            statement === undefined ||
            claimId === undefined ||
            quote === undefined
        )
            return [];
        const page = num(entry?.get("page"));
        return [
            {
                material: {
                    type: "material",
                    id,
                    title: str(entry?.get("materialTitle")) ?? "Untitled",
                },
                node,
                statement,
                claimId,
                quote,
                ...(page === undefined ? {} : { page }),
            },
        ];
    });

// `view.derivedMaterials` is a list of `{ kind, id, title }` lineage refs.
// Only material entries render in the source view; a malformed entry is
// dropped rather than failing the view.
const derivedMaterialsOf = (
    view: ReadonlyMap<string, unknown> | undefined,
): readonly MaterialRef[] =>
    list(view?.get("derivedMaterials")).flatMap((raw): MaterialRef[] => {
        const entry = fields(raw);
        const id = str(entry?.get("id"));
        if (id === undefined || str(entry?.get("kind")) !== "material")
            return [];
        return [
            {
                type: "material",
                id,
                title: str(entry?.get("title")) ?? "Untitled",
            },
        ];
    });

// `view.annotations` is the live-marks projection: `{ id, text, category,
// locator?, quote? }` (`@bilda/app`'s `ResourceAnnotationView`). A part
// locator is the adapter's own block id vocabulary (`block-N`) fed back —
// passed through as `blockId`; a whole-source mark has none. `category`
// is REQUIRED on the wire (every mark the domain emits carries one); an
// entry missing it is malformed and dropped, same as a missing id/text —
// never defaulted, per parse-don't-validate (a silently-defaulted
// category would be a guess the wire never actually asserted).
const annotationsOf = (
    view: ReadonlyMap<string, unknown> | undefined,
): readonly SourceAnnotation[] =>
    list(view?.get("annotations")).flatMap((raw): SourceAnnotation[] => {
        const entry = fields(raw);
        const id = str(entry?.get("id"));
        const text = str(entry?.get("text"));
        const category = str(entry?.get("category"));
        if (
            id === undefined ||
            text === undefined ||
            (category !== "comment" && category !== "highlight")
        ) {
            return [];
        }
        const locator = str(entry?.get("locator"));
        const quote = str(entry?.get("quote"));
        return [
            {
                id,
                text,
                category,
                ...(locator === undefined ? {} : { blockId: locator }),
                ...(quote === undefined ? {} : { quote }),
            },
        ];
    });

// === Meta =====================================================================

// The wire carries no media-type on a source (bytes are forgotten at ingest;
// `provenance.hint` is a free string). `classification` is an INTERNAL origin
// marker ("standalone", …), NOT a file kind — surfacing it as the kind badge
// leaked "Standalone" to the author (T2). We no longer show it: an unknown
// file kind is the empty string, which the view renders as a neutral localised
// "Source". `httpPorts` still overrides with "PDF" when the original-bytes
// probe recognises one — the only file kind we can name honestly here.
const kindOf = (_classification: string | undefined): string => "";

// `resource.summary` is the domain's SourceSummary; `superSummary` is the
// author-facing text (manual writes land there too). Empty ⇒ none.
const summaryOf = (
    resource: ReadonlyMap<string, unknown>,
): string | undefined => {
    const superSummary = str(
        fields(resource.get("summary"))?.get("superSummary"),
    );
    return superSummary === undefined || superSummary.trim().length === 0
        ? undefined
        : superSummary;
};

// `view.timestamps.createdAt` is an ISO-8601 wall-clock string (the app-layer
// projection). Passed through RAW per SourceMeta's contract — the view
// localises it at render against the active locale (no English/en-GB baked at
// the boundary). Absent/unparseable ⇒ undefined (the view prints no line).
const addedOnIsoOf = (
    view: ReadonlyMap<string, unknown> | undefined,
): string | undefined => {
    const iso = str(fields(view?.get("timestamps"))?.get("createdAt"));
    if (iso === undefined) return undefined;
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return undefined;
    return iso;
};

// === Content ==================================================================

// The Flat projection parse lives with the projection type
// (`SourceView/readingShape.projectText`) — parsed ONCE, reused as the shell's
// data seam. Here we only compute which block ids a note anchors to, so the
// parse can derive `highlighted` blocks; empty content degrades to the honest
// `unavailable` floor inside `projectText`.
//
// `view.extract` is the server's resolved text: `extract_available` carries
// it; `extract_missing` means the stored content is GONE (blob missing) —
// its own `content-missing` reason, so the view can offer "Upload again"
// instead of the generic "can't be read here yet". Absent/unknown ⇒ empty.
const toContent = (
    extract: ReadonlyMap<string, unknown> | undefined,
    annotations: readonly SourceAnnotation[],
): LoadedSource["content"] => {
    if (str(extract?.get("type")) === "extract_missing") {
        return { type: "unavailable", reason: { type: "content-missing" } };
    }
    const anchoredIds = new Set(
        annotations.flatMap((a) =>
            a.blockId === undefined ? [] : [a.blockId],
        ),
    );
    return projectText(str(extract?.get("text")) ?? "", anchoredIds);
};
